// The one place the product's user-visible identity lives: its name, its logo
// mark and the avatars it generates. Frontends, emails, API error messages and
// MCP tool names all read from here, so renaming the product is a one-line
// change. Internal identifiers (package names, env vars, headers, tables) keep
// their upstream names on purpose.

export let brand = {
  /** Product name shown to people: page titles, emails, MCP tool names. */
  name: 'BlackOS',
  /** Lowercase identifier form of the name, for keys such as MCP server names. */
  slug: 'blackos',
  /** Organization that runs this deployment (email footers, legal copy). */
  operator: 'Blackboard Studio',
  /** Main colour of the logo mark. */
  color: '#111111',
  /** Colour drawn on top of `color` inside the mark. */
  accentColor: '#ffffff'
} as const;

export let brandName = brand.name;

// The mark is a rounded square with a ring in it, drawn on a 56x56 grid. It
// renders without fonts, so it looks the same in every browser and email client.
export let brandMark = {
  viewBox: '0 0 56 56',
  square: { width: 56, height: 56, rx: 14 },
  ring: { cx: 28, cy: 28, r: 11.5, strokeWidth: 6.5 }
} as const;

export let brandMarkSvg = (
  opts: {
    size?: number | string;
    color?: string;
    accentColor?: string;
    /** Draw square corners, for icons the OS masks itself (apple-touch, PWA). */
    fullBleed?: boolean;
  } = {}
) => {
  let color = opts.color ?? brand.color;
  let accent = opts.accentColor ?? brand.accentColor;
  let size = opts.size ?? 56;
  let { square, ring } = brandMark;
  let rx = opts.fullBleed ? 0 : square.rx;

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${brandMark.viewBox}" width="${size}" height="${size}">` +
    `<rect width="${square.width}" height="${square.height}" rx="${rx}" fill="${color}"/>` +
    `<circle cx="${ring.cx}" cy="${ring.cy}" r="${ring.r}" fill="none" stroke="${accent}" stroke-width="${ring.strokeWidth}"/>` +
    `</svg>`
  );
};

let toSvgDataUrl = (svg: string) => `data:image/svg+xml,${encodeURIComponent(svg)}`;

/** The logo mark as a data URL, for <img> tags that must not load anything remote. */
export let brandMarkDataUrl = (opts?: Parameters<typeof brandMarkSvg>[0]) =>
  toSvgDataUrl(brandMarkSvg(opts));

// Generated avatars replace Metorial's avatar CDN. Each seed (usually an
// entity id) always maps to the same two-colour gradient.

let hashSeed = (seed: string) => {
  let hash = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    hash ^= seed.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
};

export let generatedAvatarSvg = (seed: string) => {
  let hash = hashSeed(seed || 'avatar');
  let hueA = hash % 360;
  let hueB = (hueA + 35 + ((hash >>> 9) % 90)) % 360;
  let angle = (hash >>> 17) % 4;
  let [x1, y1, x2, y2] = [
    [0, 0, 1, 1],
    [1, 0, 0, 1],
    [0, 1, 1, 0],
    [0.5, 0, 0.5, 1]
  ][angle]!;

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" data-avatar="generated" viewBox="0 0 64 64" width="64" height="64">` +
    `<defs><linearGradient id="g" x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}">` +
    `<stop offset="0" stop-color="hsl(${hueA}, 72%, 62%)"/>` +
    `<stop offset="1" stop-color="hsl(${hueB}, 68%, 46%)"/>` +
    `</linearGradient></defs>` +
    `<rect width="64" height="64" fill="url(#g)"/>` +
    `</svg>`
  );
};

export let generatedAvatarDataUrl = (seed: string) => toSvgDataUrl(generatedAvatarSvg(seed));

let generatedAvatarPrefix = toSvgDataUrl(
  '<svg xmlns="http://www.w3.org/2000/svg" data-avatar="generated"'
);

let hostedAvatarHosts = ['avatar-cdn.metorial.com', 'avatar.metorial-cdn.com'];

/** True for avatars generated here or by Metorial's avatar CDN (not uploaded by a person). */
export let isGeneratedAvatarUrl = (url: string | null | undefined) => {
  if (!url) return false;
  if (url.startsWith(generatedAvatarPrefix)) return true;

  try {
    return hostedAvatarHosts.includes(new URL(url).hostname);
  } catch {
    return false;
  }
};

let isMetorialLogoUrl = (parsed: URL) =>
  parsed.hostname === 'cdn.metorial.com' &&
  (parsed.pathname.includes('/logos/metorial/') || /\/metorial[^/]*\.svg$/.test(parsed.pathname));

/**
 * Upstream code and records created before the rebrand can still point at
 * Metorial's CDN: generated avatars (avatar-cdn) and the Metorial logo. This
 * swaps them for a local avatar with the same seed, or the brand mark, so the
 * browser never loads them. Any other URL is returned unchanged.
 */
export let localizeImageUrl = <T extends string | null | undefined>(url: T): T | string => {
  if (!url || typeof url !== 'string') return url;

  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return url;
  }

  if (hostedAvatarHosts.includes(parsed.hostname)) {
    let seed = parsed.pathname.replace(/^\/+/, '') || parsed.search || url;
    return generatedAvatarDataUrl(seed);
  }

  if (isMetorialLogoUrl(parsed)) return brandMarkDataUrl();

  return url;
};

/** @deprecated use localizeImageUrl */
export let localizeAvatarUrl = localizeImageUrl;
